import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import type { Me } from "../../api/types";
import { S } from "../../styles";
import { hasRole } from "../../auth/roleHelpers";
import { LoadingState } from "../../components/QueryStates";

export function ConsolidationPanel({ me }: { me: Me | null }) {
  const queryClient = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);

  const canSubmit = hasRole(me, "buyer", "planner");
  const canTrigger = hasRole(me, "manager", "admin");

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["consolidation-opportunities"],
    queryFn: () => api.listConsolidationOpportunities(),
  });
  const groups = data?.items ?? [];
  const pendingGroups = groups.filter((g) => g.status === "pending");

  const invalidate = () =>
    void queryClient.invalidateQueries({
      queryKey: ["consolidation-opportunities"],
    });

  const submitMutation = useMutation({
    mutationFn: (groupId: string) => api.submitConsolidatedRFQ(groupId),
    onSuccess: (r) => {
      setMsg(`✅ ${r.message}`);
      invalidate();
    },
    onError: () => setMsg("❌ Failed to submit consolidated RFQ."),
  });

  const dismissMutation = useMutation({
    mutationFn: (groupId: string) =>
      api.dismissConsolidationOpportunity(groupId),
    onSuccess: () => {
      setMsg("Group dismissed.");
      invalidate();
    },
    onError: () => setMsg("❌ Failed to dismiss group."),
  });

  const triggerMutation = useMutation({
    mutationFn: () => api.triggerConsolidationScan(),
    onSuccess: (r) => setMsg(`🔍 Scan started: ${r.workflow_id}`),
    onError: () => setMsg("❌ Failed to trigger scan."),
  });

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <h2 style={S.h2}>📦 Consolidation Opportunities</h2>
        <button style={S.btnSm} onClick={() => void refetch()}>
          ↺ Refresh
        </button>
        {canTrigger && (
          <button
            style={S.btn}
            onClick={() => triggerMutation.mutate()}
            disabled={triggerMutation.isPending}
          >
            {triggerMutation.isPending ? "Scanning…" : "🔍 Scan Now"}
          </button>
        )}
      </div>
      {msg && (
        <div style={{ ...S.card, background: "#e8f5e9", marginBottom: 12 }}>
          {msg}
        </div>
      )}
      {isLoading ? (
        <LoadingState label="Loading consolidation opportunities…" />
      ) : pendingGroups.length === 0 ? (
        <div style={S.card}>
          <p style={S.muted}>No pending consolidation opportunities.</p>
          <p style={S.muted}>
            Opportunities appear when multiple open requirements exist for the
            same part within the consolidation time window.
          </p>
        </div>
      ) : (
        <div>
          {pendingGroups.map((g) => (
            <div key={g.id} style={{ ...S.card, marginBottom: 12 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>
                    {g.requirement_ids.length} requirements — {g.total_quantity}{" "}
                    units total
                  </div>
                  <div style={S.muted}>
                    Condition: {g.condition_code} · Stations:{" "}
                    {g.station_codes.join(", ") || "—"}
                  </div>
                  {g.potential_savings_pct !== null && (
                    <div
                      style={{
                        color: "#2e7d32",
                        marginTop: 4,
                        fontWeight: 600,
                      }}
                    >
                      💰 Estimated savings: {g.potential_savings_pct.toFixed(1)}
                      %
                    </div>
                  )}
                  {g.reasoning && (
                    <p
                      style={{ ...S.muted, marginTop: 6, fontStyle: "italic" }}
                    >
                      {g.reasoning}
                    </p>
                  )}
                </div>
                {canSubmit && (
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      style={S.btn}
                      onClick={() => submitMutation.mutate(g.id)}
                      disabled={submitMutation.isPending}
                    >
                      Submit RFQ
                    </button>
                    <button
                      style={S.btnGhost}
                      onClick={() => dismissMutation.mutate(g.id)}
                      disabled={dismissMutation.isPending}
                    >
                      Dismiss
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
